import { Button, Input } from "@devhop/ui";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { useCallback } from "react";
import { useCategoriesQuery } from "@/modules/categories/presentation/api/queries";
import { InfiniteCombobox } from "@/shared/ui/InfiniteCombobox";
import { SimpleSelect } from "@/shared/ui/SimpleSelect";
import {
  brandGetLabel,
  brandGetValue,
  brandLookupQueryFn,
  brandLookupQueryKey,
  brandPreloadQueryFn,
} from "../api/brandLookup";
import type { BrandDTO } from "../api/client";
import { CategoryTreePopover } from "./CategoryTreePopover";

const ALL_VALUE = "__all__"; // Radix Select ບໍ່ອະນຸຍາດ value=""

const STATUS_OPTIONS = [
  { value: ALL_VALUE, label: "ທຸກສະຖານະ" },
  { value: "draft", label: "ຮ່າງ" },
  { value: "active", label: "ໃຊ້ງານ" },
  { value: "hidden", label: "ເຊື່ອງ" },
  { value: "archived", label: "ເກັບມ້ຽນ" },
];

const TYPE_OPTIONS = [
  { value: ALL_VALUE, label: "ທຸກປະເພດ" },
  { value: "part_new", label: "ອາໄຫຼແທ້" },
  { value: "part_oem", label: "ອາໄຫຼທຽບ (OEM)" },
  { value: "part_used", label: "ອາໄຫຼຖອດແທ້" },
  { value: "service", label: "ບໍລິການ" },
];

type CatalogProductsSearch = {
  limit?: number;
  offset?: number;
  status?: string;
  categoryId?: string;
  brandId?: string;
  type?: string;
  search?: string;
};

export function CatalogProductsFilter() {
  const nav = useNavigate({ from: "/app/catalog" });
  const search = useSearch({ from: "/app/catalog" }) as CatalogProductsSearch;
  const categoriesQuery = useCategoriesQuery({ limit: 1000, offset: 0 });
  const categoryItems = (categoriesQuery.data?.data ?? []).map((c) => ({
    id: c.id,
    name: c.name,
    parentId: c.parentId,
  }));

  const toSelectValue = (v: string | undefined) =>
    v && v !== "" ? v : ALL_VALUE;
  const fromSelectValue = (v: string) => (v === ALL_VALUE ? undefined : v);

  const update = useCallback(
    (updates: Partial<CatalogProductsSearch>) => {
      nav({
        search: {
          limit: search.limit ?? 20,
          offset: 0,
          status: search.status,
          categoryId: search.categoryId,
          brandId: search.brandId,
          type: search.type,
          search: search.search,
          ...updates,
        },
      });
    },
    [nav, search],
  );

  const hasFilter =
    (search.status && search.status !== ALL_VALUE) ||
    (search.categoryId && (search.categoryId as string).trim() !== "") ||
    (search.brandId && (search.brandId as string).trim() !== "") ||
    (search.type && search.type !== ALL_VALUE) ||
    (search.search && search.search.trim() !== "");

  return (
    <div className="mb-4 flex flex-wrap items-center gap-3 px-2">
      <Input
        placeholder="ຄົ້ນຫາຕາມຊື່..."
        value={search.search ?? ""}
        onChange={(e) => update({ search: e.target.value || undefined })}
        className="h-8 w-48"
      />
      <div className="w-64 max-w-full">
        <CategoryTreePopover
          categories={categoryItems}
          value={search.categoryId ?? ""}
          onChange={(v) => update({ categoryId: v || undefined })}
          isLoading={categoriesQuery.isLoading}
          clearable
        />
      </div>
      <SimpleSelect
        value={toSelectValue(search.status)}
        onValueChange={(v) => update({ status: fromSelectValue(v) })}
        options={STATUS_OPTIONS}
        placeholder="ສະຖານະ"
        className="h-8 w-36"
      />
      <SimpleSelect
        value={toSelectValue(search.type)}
        onValueChange={(v) => update({ type: fromSelectValue(v) })}
        options={TYPE_OPTIONS}
        placeholder="ປະເພດ"
        className="h-8 w-36"
      />
      <div className="w-40">
        <InfiniteCombobox<BrandDTO>
          queryKey={[...brandLookupQueryKey]}
          queryFn={brandLookupQueryFn}
          preloadQueryFn={brandPreloadQueryFn}
          getLabel={brandGetLabel}
          getValue={brandGetValue}
          value={search.brandId ?? ""}
          onValueChange={(v) => update({ brandId: v || undefined })}
          placeholder="ແບຣນດ໌"
          className="h-8"
          clearable
        />
      </div>
      {hasFilter && (
        <Button
          variant="ghost"
          size="sm"
          onClick={() =>
            update({
              status: undefined,
              categoryId: undefined,
              brandId: undefined,
              type: undefined,
              search: undefined,
            })
          }
        >
          ລ້າງຕົວກອງ
        </Button>
      )}
    </div>
  );
}
